<script>
import Swal from "sweetalert2";

export default {
    name: "ApiAttachmentsModal",
    props: {
        title: {
            type: String,
            default: "API Attachments",
        },
        wActions: {
            type: Boolean,
            default: false,
        },
        header: {
            type: String,
            default: "",
        },
        subheader: {
            type: Object,
            default: () => ({}),
        },
        size: {
            type: String,
            default: "lg",
        },
    },
    data() {
        return {
            apiList: [],
            filter: "",
            attachmentLoading: false,
            uploadOpt: {
                type: "API Special Projects",
                placement: "left",
                folder: "Office",
            },
        };
    },
    computed: {
        totalAttachmentsCount() {
            return this.apiList.reduce(
                (acc, curr) => acc + (curr.attachments ? curr.attachments.length : 0),
                0
            );
        },
        filteredApis() {
            if (!this.filter || !this.filter.trim()) {
                return this.apiList.map((item) => ({
                    ...item,
                    displayAttachments: item.attachments || [],
                }));
            }
            const term = this.filter.trim().toUpperCase();
            const results = [];
            for (const item of this.apiList) {
                const apiNameMatches = (item.name || "").toUpperCase().includes(term);
                const matchingAttachments = (item.attachments || []).filter((att) => {
                    const descMatches =
                        att.description && att.description.toUpperCase().includes(term);
                    const fileMatches =
                        att.fileName && att.fileName.toUpperCase().includes(term);
                    return descMatches || fileMatches;
                });

                if (apiNameMatches) {
                    results.push({
                        ...item,
                        displayAttachments:
                            matchingAttachments.length > 0
                                ? matchingAttachments
                                : item.attachments,
                    });
                } else if (matchingAttachments.length > 0) {
                    results.push({
                        ...item,
                        displayAttachments: matchingAttachments,
                    });
                }
            }
            return results;
        },
    },
    methods: {
        async getAttachments(apis) {
            this.filter = "";
            this.apiList = [];
            this.attachmentLoading = true;
            this.$bvModal.show("api-attachments-modal");

            const list = Array.isArray(apis) ? apis : apis ? [apis] : [];
            const uniqueApis = [];
            const seenIds = new Set();
            for (const item of list) {
                if (item && item.id && !seenIds.has(item.id)) {
                    seenIds.add(item.id);
                    uniqueApis.push(item);
                }
            }

            if (uniqueApis.length === 0) {
                this.attachmentLoading = false;
                return;
            }

            let errorOccurred = false;
            let errorMessage = "";
            const fetchedApis = [];

            await Promise.all(
                uniqueApis.map(async (api) => {
                    try {
                        const res = await this.$store.dispatch("filemanager/GetFiles", {
                            id: api.id,
                            type: this.uploadOpt.type,
                            placement: this.uploadOpt.placement,
                            folder: this.uploadOpt.folder,
                            removeLoading: true,
                        });

                        if (res.data && res.data.error) {
                            errorOccurred = true;
                            errorMessage = res.data.errorMessage || errorMessage;
                            return;
                        }

                        const files = Array.isArray(res.data) ? res.data : [];
                        // If no records found, don't display the api
                        if (files.length > 0) {
                            fetchedApis.push({
                                id: api.id,
                                name:
                                    api.name ||
                                    api.description ||
                                    api.title ||
                                    `API #${api.id}`,
                                rawApi: api,
                                attachments: files,
                            });
                        }
                    } catch (err) {
                        errorOccurred = true;
                    }
                })
            );

            // Preserve initial order of uniqueApis
            this.apiList = uniqueApis
                .map((u) => fetchedApis.find((f) => f.id === u.id))
                .filter(Boolean);

            this.attachmentLoading = false;

            if (errorOccurred && this.apiList.length === 0) {
                this.showToast(
                    errorMessage || "Something went wrong! - getting documents",
                    "error"
                );
            }
        },
        getCount() {
            return this.totalAttachmentsCount;
        },
        setUploadOpt(opt) {
            this.uploadOpt = Object.assign({}, this.uploadOpt, opt);
        },
        openDocument(x) {
            this.readFile(x.id);
        },
        formatDateSafe(dt) {
            if (!dt) return "";
            try {
                const d = new Date(dt);
                if (isNaN(d.getTime())) return "";
                return this.formatDateWithTime
                    ? this.formatDateWithTime(d)
                    : d.toLocaleString();
            } catch {
                return "";
            }
        },
        removeFile(id, apiId) {
            Swal.fire({
                title: "Are you sure?",
                text: "You won't be able to revert this!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#dc3545",
                cancelButtonColor: "#5c636a",
                confirmButtonText: "Yes, remove it!",
            }).then((result) => {
                if (result.value) {
                    this.$store
                        .dispatch("filemanager/DeleteFile", id)
                        .then((res) => {
                            if (res.data && res.data.error) {
                                return this.showToast(res.data.errorMessage, "error");
                            }
                            const targetApi = this.apiList.find((a) => a.id === apiId);
                            if (targetApi && targetApi.attachments) {
                                const index = targetApi.attachments.findIndex(
                                    (item) => item.id == id
                                );
                                if (index !== -1) {
                                    targetApi.attachments.splice(index, 1);
                                }
                            }
                            // If no records found, don't display the api
                            this.apiList = this.apiList.filter(
                                (a) => a.attachments && a.attachments.length > 0
                            );
                            this.showToast("Successfully removed!", "success");
                        })
                        .catch(() => {
                            this.showToast("Cannot be remove!", "error");
                        });
                }
            });
        },
        hideModal() {
            this.$bvModal.hide("api-attachments-modal");
        },
        onHidden() {
            this.filter = "";
            this.$emit("close");
        },
    },
};
</script>

<template>
    <b-modal id="api-attachments-modal" :title="title" :size="size" title-class="font-18" hide-footer
        no-close-on-backdrop no-close-on-esc @hidden="onHidden">
        <h5 class="mb-1" v-if="header">{{ header }}</h5>
        <h6 class="mb-4" v-if="subheader && subheader.title" :class="[subheader.class]">
            {{ subheader.title }}
        </h6>

        <div class="row">
            <div class="col-md-6">
                <input type="search" class="form-control" placeholder="Search Attachments" v-model="filter" />
            </div>
        </div>

        <div>
            <h5 class="mt-3" v-if="!attachmentLoading && totalAttachmentsCount == 0">
                No document found.
            </h5>
            <h5 class="mt-3"
                v-if="!attachmentLoading && totalAttachmentsCount > 0 && filteredApis.length == 0 && filter">
                Search not found.
            </h5>
        </div>

        <div class="
                d-flex
                justify-content-center
                align-items-center
                ms-4
                font-size-14
                mt-4
            " v-if="attachmentLoading">
            <div class="preloader-component me-2">
                <div class="status">
                    <div class="spinner-chase w-20px h-20px">
                        <div class="chase-dot"></div>
                        <div class="chase-dot"></div>
                        <div class="chase-dot"></div>
                        <div class="chase-dot"></div>
                        <div class="chase-dot"></div>
                        <div class="chase-dot"></div>
                    </div>
                </div>
            </div>
            <strong>Loading...</strong>
        </div>

        <div class="mt-3" v-if="filteredApis.length > 0 && !attachmentLoading">
            <div v-for="apiItem in filteredApis" :key="apiItem.id" class="api-attachment-group mb-4">
                <!-- Display API Header -->
                <div class="d-flex align-items-center justify-content-between p-2 px-3 rounded border mb-2">
                    <div class="d-flex align-items-center overflow-hidden me-2">
                        <i class="bx bx-layer font-size-18 text-primary me-2 flex-shrink-0"></i>
                        <h6 class="font-size-14 fw-bold text-dark mb-0" :title="apiItem.name">
                            {{ apiItem.name }}
                        </h6>
                    </div>
                </div>

                <!-- Below the API are the attachments attached to it -->
                <div class="row">
                    <div class="col-md-6" v-for="y in apiItem.displayAttachments" :key="y.id">
                        <div class="
                                position-relative
                                cursor-pointer
                                border
                                mt-2
                                br-5
                            ">
                            <div class="border p-3">
                                <div>
                                    <div class="float-end ms-2">
                                        <b-dropdown :id="`dropdown-dropleft-${apiItem.id}-${y.id}`" right variant="link"
                                            toggle-class="text-decoration-none text-dark font-size-16 pt-0"
                                            menu-class="dropdown-menu-end" no-caret>
                                            <template #button-content>
                                                <i class="mdi mdi-dots-horizontal"></i>
                                            </template>
                                            <b-dropdown-item @click="openDocument(y)">
                                                <i class="bx bx-link-external me-1"></i>Open
                                            </b-dropdown-item>
                                            <b-dropdown-item variant="danger" @click="removeFile(y.id, apiItem.id)"
                                                v-if="wActions">
                                                <i class="bx bx-trash me-1"></i>Remove
                                            </b-dropdown-item>
                                        </b-dropdown>
                                    </div>
                                    <div class="avatar-xs me-3 mb-2" @click="openDocument(y)">
                                        <div class="
                                                avatar-title
                                                bg-transparent
                                                rounded
                                            ">
                                            <i v-if="y.fileType && y.fileType.includes('image')" class="
                                                    mdi mdi-image
                                                    font-size-24
                                                    text-purple
                                                "></i>
                                            <i v-else-if="y.fileType && y.fileType.includes('application')" class="
                                                    mdi mdi-file-pdf-outline
                                                    font-size-24
                                                    text-danger
                                                "></i>
                                            <i v-else class="
                                                    mdi mdi-file-document-outline
                                                    font-size-24
                                                    text-primary
                                                "></i>
                                        </div>
                                    </div>
                                    <div class="d-flex" @click="openDocument(y)">
                                        <div class="overflow-hidden me-auto">
                                            <h5 class="
                                                    font-size-14
                                                    text-truncate
                                                    mb-1
                                                " :title="y.description">
                                                {{ y.description }}
                                            </h5>
                                            <p class="text-muted mb-0">
                                                {{ formatDateSafe(y.dateTimeUploaded) }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </b-modal>
</template>
